/**
 * The tRPC client. Spec 4.5.
 *
 * "Types are imported directly from @lila/api via the monorepo; there is no code
 * generation step and no OpenAPI schema to drift."
 */
import type { AppRouter } from '@lila/api';
import type { QueryClient } from '@tanstack/react-query';
import { createTRPCClient, httpBatchLink } from '@trpc/client';
import { createTRPCOptionsProxy } from '@trpc/tanstack-react-query';
import superjson from 'superjson';

export const trpcClient = createTRPCClient<AppRouter>({
  links: [
    httpBatchLink({
      url: '/api/trpc',
      transformer: superjson,
      /*
       * Spec 10.2 and hard rule 7: the session is an HttpOnly cookie, never a token in
       * localStorage. `include` is what makes the browser attach it — without this the SPA
       * is permanently anonymous.
       */
      fetch: (url, options) =>
        /*
         * `signal` is spread explicitly because tRPC types it as `AbortSignal | undefined`
         * while `RequestInit` wants `AbortSignal | null`; under
         * `exactOptionalPropertyTypes` a plain spread is not assignable.
         */
        fetch(url, {
          ...options,
          credentials: 'include',
          signal: options?.signal ?? null,
        }),
    }),
  ],
});

/*
 * The return type is deliberately inferred: it IS the contract — a proxy mirroring every
 * procedure in AppRouter — and naming it would mean reproducing that tree by hand.
 */
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function createTrpcProxy(queryClient: QueryClient) {
  return createTRPCOptionsProxy<AppRouter>({ client: trpcClient, queryClient });
}

export type TrpcProxy = ReturnType<typeof createTrpcProxy>;
